/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { omit } from 'lodash';

// Components
import BaseControl from './BaseControl.vue';

/**
 * Base component to wrap select with a common markup.
 *
 * Note that this is a wrapper component to VueMultiselect: for this purpose we
 * bind attributes and listeners directly to the plugin component via `v-bind` and `v-on`.
 * This is basically how you do pass-through in Vue.js.
 *
 * It has all `BaseControl` options, plus all vue-multiselect props, plus:
 *
 * @vue-prop {String} [optionsLabel=null] the key path to use for the label of every option, if not found the value field is used
 *
 * @example
 *
 * base-select(:label="t('field')" :placeholder="t('field_placeholder')" name="field" v-model="form.field" validations="required" :options="[]")
 *
 * @see https://vue-multiselect.js.org/#sub-props
 *
 * @category ui.components.form-elements
 * @exports BaseSelect
 * @component
 */
const __vue_script__ = {
    name: 'BaseSelect',

    extends: BaseControl,

    inheritAttrs: false,

    props: {
        // Have to overwrite this here because BaseControl
        // declares one.
        value: {
            type: [Object, Array, String, Number],
            default: null
        },

        optionsLabel: {
            type: String,
            default: 'label'
        },

        openDirection: {
            type: String,
            default: null
        },

        disabled: {
            type: Boolean,
            default: false
        },

        closeOnSelect: {
            type: Boolean,
            default: true
        },

        emitOptionValueOnly: {
            type: Boolean,
            default: false
        },

        allowEmpty: {
            type: Boolean,
            default: true
        },

        multiple: {
            type: Boolean,
            default: false
        },

        groupSelect: {
            type: Boolean,
            default: false
        },

        groupLabel: {
            type: String,
            default: null
        },

        groupValues: {
            type: String,
            default: null
        },

        options: {
            type: Array,
            default: () => []
        },

        trackBy: {
            type: String,
            default: null
        }
    },

    computed: {
        innerValue: {
            get() {
                let value = this.value;

                if (this.emitOptionValueOnly) {
                    if (this.attrs.multiple) {
                        value = this.attrs.options.filter(option => value.includes(option.value));
                    } else if (value) {
                        // There are cases where the value is a number and the option value is a string.
                        // See for example the duration field in the meeting document.
                        value = this.attrs.options.find(option => option.value === (value).toString());
                    }
                }

                return value;
            },

            set(value) {
                this.input(value);
            }
        },

        // Pass-through listeners
        listeners() {
            // Here we skip the `input` listener, since we provide our own
            // and we don't want it to be overwritten.

            // eslint-disable-next-line
            const { input, open, close, ...listeners } = this.$listeners;

            return { ...listeners, open: () => this.onOpen(), close: () => this.onClose() };
        },

        // pass-through slots other than noResult slot which is handled locally
        filteredSlots() {
            return Object.entries(this.$scopedSlots)
                /* eslint-disable-next-line no-unused-vars */
                .filter(([name, _]) => name !== 'noResult')
                .reduce((acc, [name, slot]) => ({ ...acc, [name]: slot }), {});
        },

        // Pass-through attributes
        attrs() {
            /* eslint-disable no-unused-vars */

            // Here we remove all the keys that we don't want `multiselect`
            // to inherit from this very component. All other options and attributes
            // are passed through. Basically an underscore.pluck, but since it has been
            // deprecated we are doing it manually.
            const attrs = omit(Object.assign(this.$options.propsData, this.$attrs), [
                'autocomplete',
                'edit',
                'emitKey',
                'errors',
                'eventid',
                'fpType',
                'hasDefault',
                'hint',
                'hintAfterLabel',
                'id_field',
                'label',
                'legacy',
                'model',
                'optionsLabel',
                'preview',
                'showcreatebutton',
                'single_doc',
                'sortable',
                'statuses',
                'timezone',
                'tip',
                'type',
                'value'
            ]);

            return attrs;
        },

        showRemoveButton() {
            if (this.disabled) {
                return false;
            }

            let hasValue = false;
            if (Array.isArray(this.innerValue)) {
                hasValue = this.innerValue.length > 0;
            } else if (this.innerValue && typeof this.innerValue === 'object') {
                hasValue = Object.keys(this.innerValue).length > 0;
            }

            return this.allowEmpty && !this.multiple && hasValue;
        }
    },

    methods: {
        /**
         * Triggered when user interacts with input components.
         *
         * @overwrites BaseControl.input
         *
         * @param {Object} payload the selected payload
         */
        input(payload) {
            if (this.emitOptionValueOnly) {
                if (payload?.value) {
                    payload = payload.value;
                } else if (this.attrs.multiple) {
                    payload = payload.map(option => option.value);
                }
            }

            this.$emit('input', payload);
        },

        /**
         * Tries to grab the focus on the input element
         */
        attemptFocus() {
            const select = this.$refs.select;

            if (this.focused && select && select.$refs.search) {
                select.$refs.search.focus();
            }
        },

        /**
         * Hook for the opening of the dropdown menu
         */
        onOpen() {
            const ref = this.$refs['select'];
            if (!ref) {
                console.warn('[BaseSelect] Element not in DOM yet.');
                return;
            }

            const inModal = ref.$el.closest('.v--modal, .v--modal-box') !== null;

            if (inModal) {
                this.calculateMenuPosition(ref);

                const scrollingParent = this.$utils.dom.getScrollParent(ref.$el);
                scrollingParent.addEventListener('scroll', () => this.calculateMenuPosition(ref));
            }

            this.$emit('open');
        },

        /**
         * Hook for the closing of the dropdown menu
         */
        onClose() {
            const ref = this.$refs['select'];
            if (!ref) {
                console.warn('[BaseSelect] Element already removed from the DOM.');
                return;
            }

            const scrollingParent = this.$utils.dom.getScrollParent(ref.$el);
            scrollingParent.removeEventListener('scroll', () => this.calculateMenuPosition(ref));

            this.$emit('close');
        },

        /**
         * Calculates the correct position of the dropdown menu
         *
         * @param {object} ref the multiselect reference
         */
        calculateMenuPosition(ref) {
            requestAnimationFrame(() => {
                const parent = ref.$el.closest('.v--modal, .v--modal-box');
                if (!parent) {
                    return;
                }

                const { top: pTop, bottom: pBottom } = parent.getBoundingClientRect();
                const { top, height, width } = ref.$el.getBoundingClientRect();
                let { height: mHeight } = ref.$refs.list.getBoundingClientRect();

                try {
                    const mTop = Number.parseInt(window.getComputedStyle(ref.$refs.list)['margin-top']);
                    const mBottom = Number.parseInt(window.getComputedStyle(ref.$refs.list)['margin-bottom']);
                    mHeight += mTop + mBottom;
                } catch (error) {
                    console.warn('[BaseSelect] Could not determine dropdown margins', error);
                }

                ref.$refs.list.style.width = `${width}px`;
                ref.$refs.list.style.position = 'fixed';
                ref.$refs.list.style.bottom = 'auto';

                const offsetTopInModal = top - pTop;
                const availableBelow = pBottom - (top + height);
                const hasSpaceAbove = offsetTopInModal >= mHeight;
                const shouldOpenAbove = this.openDirection === 'above' || (this.openDirection !== 'below' && availableBelow < mHeight && hasSpaceAbove);

                if (shouldOpenAbove) {
                    ref.$refs.list.style.top = `${top - pTop - mHeight}px`;
                } else {
                    ref.$refs.list.style.top = `${top + height - pTop}px`;
                }
            });
        },

        clean() {
            this.innerValue = null;
            this.$emit('clean');
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('control-decorator',{attrs:{"label":_vm.label,"tip":_vm.tip,"hint":_vm.hint,"uid":_vm.uid,"errors":_vm.errors,"warning":_vm.warning,"hint-after-label":_vm.hintAfterLabel,"dependant":_vm.dependant},scopedSlots:_vm._u([{key:"inside-label",fn:function(ref){return [_vm._t("inside-label")]}},{key:"after-label",fn:function(ref){return [_vm._t("after-label")]}},{key:"control-actions",fn:function(ref){return [_vm._t("control-actions")]}}],null,true)},[_c('multiselect',_vm._g(_vm._b({ref:"select",staticClass:"form-control",class:{'has-error': _vm.errors.length, 'has-warning': _vm.warning},attrs:{"disabled":_vm.disabled,"close-on-select":_vm.closeOnSelect,"id":_vm.uid,"label":_vm.optionsLabel},scopedSlots:_vm._u([{key:"caret",fn:function(ref){
var toggle = ref.toggle;
return [(_vm.showRemoveButton)?_c('button',{staticClass:"remove",attrs:{"type":"button"},on:{"click":function($event){return _vm.clean()}}},[_c('i',{staticClass:"ic-x"})]):_vm._e(),_c('div',{staticClass:"toggler"},[_c('i',{staticClass:"ic-chevron-down",on:{"mousedown":function($event){$event.preventDefault();$event.stopPropagation();return toggle.apply(null, arguments)}}})])]}},_vm._l((_vm.filteredSlots),function(_,scopedSlot){return {key:scopedSlot,fn:function(scope){return [_vm._t(scopedSlot,null,null,scope)]}}}),{key:"no-result",fn:function(ref){return [_vm._v(_vm._s(_vm.$t('general.no_elements')))]}},{key:"max-elements",fn:function(ref){return [_c('span',{staticClass:"max-elements"},[_vm._v(_vm._s(_vm.$t('general.forms.max_selection_reached', [_vm.attrs.max])))])]}}],null,true),model:{value:(_vm.innerValue),callback:function ($$v) {_vm.innerValue=$$v},expression:"innerValue"}},'multiselect',_vm.attrs,false),_vm.listeners))],1)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "BaseSelect.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__